import { createMiddleware } from '@tanstack/react-start'
import { json } from '@tanstack/react-start'
import { logtoClient, logtoConfig, LogtoScopes } from './logto/logto'
import Keyv from 'keyv'
import KeyvRedis from '@keyv/redis'
import { decodeToken } from './tokens'

import { ScopeRequirement, scopeToString, testScopes } from './scopes'
import { runtimeConfig } from './runtime-config'
import { verifyUserToken } from './tokens'
import { ErrorResponse } from './errors'
import z from 'zod'
import type * as PostHogServer from './posthog'
import { LogtoClientError, LogtoRequestError } from '@logto/browser'

// `./posthog` statically imports posthog-node, which touches `process.argv` at
// module scope and throws the moment a browser evaluates it. Server functions
// import this file to declare their `.middleware([...])`, which puts it in the
// client module graph — so a static import here crashes hydration app-wide.
// Loading it lazily keeps posthog-node off the client's evaluation path; these
// wrappers only ever run inside server-function handlers.
async function captureException(
  ...args: Parameters<typeof PostHogServer.captureException>
): Promise<void> {
  const { captureException: capture } = await import('./posthog')
  return capture(...args)
}

async function trackMutation(
  ...args: Parameters<typeof PostHogServer.trackMutation>
): Promise<void> {
  const { trackMutation: track } = await import('./posthog')
  return track(...args)
}

// export interface ScopeRule {

// }


/**
 * Extract organization ID from request path
 */
function extractOrgId(request: Request): string | undefined {
  const url = new URL(request.url)
  const pathname = url.pathname
  if (pathname.startsWith('/v1/org/')) {
    return pathname.split('/')[3]
  }
  return undefined
}



/**
 * TanStack Router middleware to require authentication
 */
export const requireAuth = createMiddleware({ type: 'function' })
  .client(async ({ next, context }) => {
    let token: string | null = null

    if (!(context as any)?.bearer) {
      const isAdminRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/')
      const isAdmin = typeof window !== 'undefined' && sessionStorage.getItem('adminMode') === 'true'
      const activeOrgId = (isAdminRoute || isAdmin) ? null : sessionStorage.getItem('activeOrgId')

      try {
        token = await logtoClient.getAccessToken(runtimeConfig.VITE_LOGTO_RESOURCE, activeOrgId ?? undefined)

        if (activeOrgId && token) {
          try {
            const claims = decodeToken(token)
            if (claims.organization_id && claims.organization_id !== activeOrgId) {
              token = await logtoClient.getAccessToken(runtimeConfig.VITE_LOGTO_RESOURCE, activeOrgId)
            }
          } catch (_) {
            // decode failure is non-fatal; proceed with the token we have
          }
        }
      } catch(error) {
        if (error instanceof LogtoRequestError) {
          if (activeOrgId != null && error.code === "oidc.access_denied") {
            sessionStorage.removeItem('activeOrgId')
            try {
              token = await logtoClient.getAccessToken(runtimeConfig.VITE_LOGTO_RESOURCE, undefined)
            } catch (fallbackError) {
              throw fallbackError
            }
          } else {
            throw error
          }
        } else {
          throw error
        }
      }
    } else {
      token = (context as any).bearer
    }

    return next({
      sendContext: {
        bearer: token,
        accessToken: null,
      },
    })
  })
  .server(
    async ({ next, data, context }) => {
      // const token = extractToken(request)
      if (!context.bearer) {
        throw new Response(
          JSON.stringify({ error: 'Missing authorization token', code: '75D1B0B6-39AE-4C8F-B202-F7E189DED9DA' }),
          {
            status: 401,
            headers: { 'Content-Type': 'application/json' }
          }
        )
      }


      const accessToken = await verifyUserToken(context.bearer)
      // (context.accessToken as unknown as AccessToken) = accessToken
      // Store user context in headers for now (TanStack Router doesn't have request context)


      return await next({
        context: {
          ...context,
          accessToken: accessToken,
          isOrgToken: !!accessToken.organization_id,
        }
      })

    }
  )



/**
 * TanStack Router middleware to require specific scopes
 */
export function requireScopes(requiredScopes: ScopeRequirement) {
  return createMiddleware({ type: 'function' })

    .middleware([requireAuth])
    // .validator((data: string) => data)
    .server(async ({ next, context, data }) => {
      // const userScopesHeader = context.accessToken?.scopes.join(',')
      if (!context.accessToken?.scopes) {
        throw new ErrorResponse('User not authenticated', 401, '5AB1F2BD-6CB6-46F2-BA3F-FFA8BB3ABDF7')
      }
      // context.accessToken.scopes
      const userScopes = context.accessToken.scopes
      const orgId = context.accessToken.organization_id
      const hasRequiredScopes = testScopes(userScopes, requiredScopes, !!orgId)

      if (!hasRequiredScopes) {
        console.error("userScopes: ", userScopes, "isOrgToken: ", context.isOrgToken, "access token", context.accessToken)
        requiredScopes.toString()
        // console.log("requiredScopes: ", requiredScopes)
        
        throw new ErrorResponse(`Missing required scopes: ${scopeToString(requiredScopes)}`, 403, 'C3C957FC-E936-4B30-AF55-0FB19B46D9CD')
      }
      return await next()
    })
}

export const organizationIdRequestSchema = z.union([z.object({
  organization_id: z.string()
}).loose(), z.object({
  organization_id: z.string().nullish()
}).loose()

])

export const requireOrgTokenToMatchOrgId = createMiddleware({ type: 'function' })

  .middleware([requireAuth])
  
  .validator(organizationIdRequestSchema)
  .server(async ({ next, context, data }) => {
    if (context.isOrgToken) {
      if (context.accessToken!.organization_id !== data.organization_id) {
        const hasAdminScope = testScopes(context.accessToken?.scopes ?? [], LogtoScopes.AdminAccess)
        if (!hasAdminScope) {
          throw new ErrorResponse('Organization ID does not match. Please use the correct organization token.', 403, 'ORG-ID-DOES-NOT-MATCH')
        }
      }
    }
    return await next()
  })


// /**
//  * TanStack Router middleware to require organization access with scopes
//  */
// export function requireOrgScopes(requiredScopes: string[]) {
//   return createMiddleware({ type: 'request' })
//     .middleware([requireAuth])
//     .server(async ({ next, request }) => {
//       const userScopesHeader = request.headers.get('x-user-scopes')
//       if (!userScopesHeader) {
//         throw new Response(
//           JSON.stringify({ error: 'User not authenticated', code: 'not-authenticated' }),
//           { 
//             status: 401,
//             headers: { 'Content-Type': 'application/json' }
//           }
//         )
//       }

//       // Extract organization ID from path
//       const orgId = extractOrgId(request)
//       if (!orgId) {
//         throw new Response(
//           JSON.stringify({ error: 'Organization ID not found in path', code: 'missing-org-id' }),
//           { 
//             status: 400,
//             headers: { 'Content-Type': 'application/json' }
//           }
//         )
//       }

//       // Get organization token from headers
//       const orgToken = request.headers.get('x-org-token')
//       if (!orgToken) {
//         throw new Response(
//           JSON.stringify({ error: 'Missing organization token', code: 'missing-org-token' }),
//           { 
//             status: 401,
//             headers: { 'Content-Type': 'application/json' }
//           }
//         )
//       }

//       try {
//         // Verify organization token
//         const orgAccessToken = await verifyOrgToken(orgToken, orgId)

//         // Check if user has required scopes (combine user and org scopes)
//         const userScopes = userScopesHeader.split(',')
//         const allScopes = [...userScopes, ...orgAccessToken.scopes]
//         const hasAllScopes = requiredScopes.every(scope => allScopes.includes(scope))

//         if (!hasAllScopes) {
//           throw new Response(
//             JSON.stringify({ 
//               error: `Missing required scopes: ${requiredScopes.join(', ')}`, 
//               code: 'insufficient-scopes',
//               required: requiredScopes,
//               provided: allScopes
//             }),
//             { 
//               status: 403,
//               headers: { 'Content-Type': 'application/json' }
//             }
//           )
//         }

//         // Store org context in headers
//         const result = await next()
//         result.response.headers.set('x-org-id', orgId)
//         result.response.headers.set('x-org-scopes', orgAccessToken.scopes.join(','))

//         return result
//       } catch (error) {
//         if (error instanceof Response) {
//           throw error
//         }
//         if (error instanceof ErrorResponse) {
//           throw new Response(
//             JSON.stringify({ error: error.message, code: error.code }),
//             { 
//               status: error.status,
//               headers: { 'Content-Type': 'application/json' }
//             }
//           )
//         }
//         throw new Response(
//           JSON.stringify({ error: 'Organization authentication failed', code: 'org-auth-error' }),
//           { 
//             status: 401,
//             headers: { 'Content-Type': 'application/json' }
//           }
//         )
//       }
//     })
// }



/**
 * Utility middleware to log requests
 */
export const requestLogger = createMiddleware({ type: 'request' }).server(
  async ({ next, request }) => {
    const start = Date.now()
    console.info(`→ ${request.method} ${request.url}`)

    const result = await next()

    const duration = Date.now() - start
    console.info(`← ${request.method} ${request.url} ${result.response.status} (${duration}ms)`)

    return result
  }
)

/**
 * Utility middleware to add CORS headers
 */
export const corsMiddleware = createMiddleware({ type: 'request' }).server(
  async ({ next, request }) => {
    const result = await next()

    // Add CORS headers
    result.response.headers.set('Access-Control-Allow-Origin', '*')
    result.response.headers.set('Access-Control-Allow-Methods', 'GET, POST, PUT, DELETE, OPTIONS')
    result.response.headers.set('Access-Control-Allow-Headers', 'Content-Type, Authorization, x-org-token')

    return result
  }
)

/**
 * Utility middleware to handle OPTIONS requests for CORS preflight
 */
export const corsPreflight = createMiddleware({ type: 'request' }).server(
  async ({ next, request }) => {
    if (request.method === 'OPTIONS') {
      const result = await next()
      result.response = new Response(null, {
        status: 204,
        headers: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
          'Access-Control-Allow-Headers': 'Content-Type, Authorization, x-org-token',
        },
      })
      return result
    }

    return await next()
  }
) 

// Rate limiting middleware (Keyv + optional Redis)

type RateLimitState = {
  count: number
  resetAt: number
}

const WINDOW_MS = Number(process.env.RATE_LIMIT_WINDOW_MS ?? 60_000)
const MAX_REQUESTS = Number(process.env.RATE_LIMIT_MAX ?? 1000)

// Prefer Redis if REDIS_URL is available; fall back to in-memory Keyv
const rateLimitStore = (() => {
  try {
    const redisUrl = process.env.REDIS_URL
    if (redisUrl) {
      const adapter = new KeyvRedis(redisUrl)
      return new Keyv<RateLimitState>({ store: adapter, namespace: 'rate-limit' })
    }
  } catch (e) {
    console.warn('Redis unavailable for rate limit store, falling back to memory.')
  }
  return new Keyv<RateLimitState>({ namespace: 'rate-limit' })
})()

async function checkAndConsumeRateLimit(key: string): Promise<{ allowed: boolean; remaining: number; resetAt: number }>
{
  const now = Date.now()
  const existing = await rateLimitStore.get(key)

  if (!existing || now >= existing.resetAt) {
    const resetAt = now + WINDOW_MS
    const state: RateLimitState = { count: 1, resetAt }
    await rateLimitStore.set(key, state, WINDOW_MS)
    return { allowed: true, remaining: Math.max(0, MAX_REQUESTS - 1), resetAt }
  }

  if (existing.count >= MAX_REQUESTS) {
    return { allowed: false, remaining: 0, resetAt: existing.resetAt }
  }

  const updated: RateLimitState = { count: existing.count + 1, resetAt: existing.resetAt }
  // Persist with remaining TTL
  const ttl = Math.max(0, updated.resetAt - now)
  await rateLimitStore.set(key, updated, ttl)
  return { allowed: true, remaining: Math.max(0, MAX_REQUESTS - updated.count), resetAt: updated.resetAt }
}

function getIpFromRequest(request: Request): string {
  const ipHeaders = [
    'x-forwarded-for',
    'x-real-ip',
    'cf-connecting-ip',
    'fastly-client-ip',
    'true-client-ip',
    'x-client-ip',
    'x-cluster-client-ip',
  ]
  
  for (const header of ipHeaders) {
    const value = request.headers.get(header)
    if (value) {
      // x-forwarded-for can contain multiple IPs, take the first one
      const ip = value.split(',')[0]!.trim()
      // Basic IP validation - check if it looks like an IP address
      if (isValidIp(ip)) {
        return ip
      }
    }
  }
  
  // Fallback if no valid proxy headers are present
  return 'unknown'
}

function isValidIp(ip: string): boolean {
  // Basic IP validation - check for IPv4 and IPv6 patterns
  const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/
  
  return ipv4Regex.test(ip) || ipv6Regex.test(ip) || ip === '127.0.0.1' || ip === '::1'
}

function getFunctionRateLimitKey(context: any): string {
  try {
    if (context?.accessToken?.sub) {
      return `user:${context.accessToken.sub}`
    }
    if (context?.bearer) {
      const decoded = decodeToken(context.bearer as string)
      if (decoded?.sub) {
        return `user:${decoded.sub}`
      }
    }
  } catch (_) {
    // ignore decode errors; fall back to anon
  }
  return 'anon:global'
}

export const rateLimitRequest = createMiddleware({ type: 'request' }).server(
  async ({ next, request }) => {
    const ip = getIpFromRequest(request)
    const path = new URL(request.url).pathname
    
    // Log IP detection issues for debugging
    if (ip === 'unknown') {
      console.warn(`Could not detect IP for request to ${path}. Headers:`, {
        'x-forwarded-for': request.headers.get('x-forwarded-for'),
        'x-real-ip': request.headers.get('x-real-ip'),
        'cf-connecting-ip': request.headers.get('cf-connecting-ip'),
      })
    }
    
    // If we can't determine the IP, use a more specific fallback
    // This prevents all requests from sharing the same rate limit
    const rateLimitKey = ip === 'unknown' 
      ? `unknown:${request.headers.get('user-agent')?.slice(0, 50) || 'no-ua'}:${path}`
      : `ip:${ip}:path:${path}`
    
    const { allowed, remaining, resetAt } = await checkAndConsumeRateLimit(rateLimitKey)
    
    if (!allowed) {
      const retryAfter = Math.ceil((resetAt - Date.now()) / 1000)
      
      // Log rate limit hits for debugging
      console.warn(`Rate limit hit for key: ${rateLimitKey}, IP: ${ip}, Path: ${path}`)
      
      throw new Response(JSON.stringify({ 
        error: 'Too many requests',
        retryAfter: Math.max(0, retryAfter),
        key: rateLimitKey // Include key for debugging
      }), {
        status: 429,
        headers: {
          'Content-Type': 'application/json',
          'Retry-After': String(Math.max(0, retryAfter)),
        },
      })
    }
    
    const result = await next()
    result.response.headers.set('X-RateLimit-Limit', String(MAX_REQUESTS))
    result.response.headers.set('X-RateLimit-Remaining', String(remaining))
    result.response.headers.set('X-RateLimit-Reset', String(Math.ceil(resetAt / 1000)))
    return result
  }
)

export const rateLimitFunction = createMiddleware({ type: 'function' }).server(
  async ({ next, context }) => {
    const key = getFunctionRateLimitKey(context)
    const { allowed } = await checkAndConsumeRateLimit(`fn:${key}`)
    if (!allowed) {
      throw new ErrorResponse('Too many requests', 429, 'RATE_LIMITED')
    }
    return await next()
  }
)

// PostHog error tracking middleware
export function withErrorTracking<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  getDistinctId: (...args: T) => string,
  getContext?: (...args: T) => Record<string, any>
) {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args)
    } catch (error) {
      const distinctId = getDistinctId(...args)
      const context = getContext ? getContext(...args) : {}
      
      await captureException(error as Error, distinctId, {
        function_name: fn.name,
        ...context
      })
      
      throw error
    }
  }
}

// PostHog mutation tracking middleware
export const posthogTracking = createMiddleware({ type: 'function' })
  .middleware([requireAuth])
  .server(async ({ next, context, data }) => {
    const startTime = Date.now()
    const userId = context.accessToken?.sub
    const organizationId = context.accessToken?.organization_id
    const isOrgToken = context.isOrgToken
    
    // Extract function name from the request URL or use a default
    const functionName = 'server_function_mutation'
    
    // Track the mutation start
    if (userId) {
      await trackMutation(
        `${functionName}_started`,
        userId,
        {
          function_name: functionName,
          is_org_token: isOrgToken,
          organization_id: organizationId,
          user_scopes: context.accessToken?.scopes || [],
        },
        organizationId
      )
    }

    try {
      // Execute the function
      const result = await next()
      
      const duration = Date.now() - startTime
      
      // Track successful mutation
      if (userId) {
        await trackMutation(
          `${functionName}_completed`,
          userId,
          {
            function_name: functionName,
            is_org_token: isOrgToken,
            organization_id: organizationId,
            user_scopes: context.accessToken?.scopes || [],
            duration_ms: duration,
            success: true,
            // Include relevant data properties (sanitized)
            data_keys: data ? Object.keys(data) : [],
          },
          organizationId
        )
      }
      
      return result
    } catch (error) {
      const duration = Date.now() - startTime
      
      // Track failed mutation
      if (userId) {
        await trackMutation(
          `${functionName}_failed`,
          userId,
          {
            function_name: functionName,
            is_org_token: isOrgToken,
            user_scopes: context.accessToken?.scopes || [],
            duration_ms: duration,
            success: false,
            error_message: error instanceof Error ? error.message : 'Unknown error',
            error_type: error instanceof Error ? error.constructor.name : 'Unknown',
            // Include relevant data properties (sanitized)
            data_keys: data ? Object.keys(data) : [],
          },
          organizationId
        )
      }
      
      throw error
    }
  })

// Helper function to create PostHog tracking middleware for specific functions
export function createPostHogTrackingMiddleware(functionName: string) {
  return createMiddleware({ type: 'function' })
    .middleware([requireAuth])
    .server(async ({ next, context, data }) => {
      const startTime = Date.now()
      const userId = context.accessToken?.sub
      const organizationId = context.accessToken?.organization_id
      const isOrgToken = context.isOrgToken
      
      // Create a mutable tracking context that can be modified by the request handler
      const trackingContext: Record<string, any> = {
        function_name: functionName,
        is_org_token: isOrgToken,
        organization_id: organizationId,
        user_scopes: context.accessToken?.scopes || [],
      }
      
      // Track the mutation start
      if (userId) {
        await trackMutation(
          `${functionName}_started`,
          userId,
          trackingContext,
          organizationId
        )
      }

      try {
        // Execute the function with the tracking context available
        const result = await next({
          context: {
            ...context,
            posthogTracking: trackingContext,
          }
        })
        
        const duration = Date.now() - startTime
        
        // Track successful mutation with any additional context added by the handler
        if (userId) {
          await trackMutation(
            `${functionName}_completed`,
            userId,
            {
              ...trackingContext,
              duration_ms: duration,
              success: true,
              // Include relevant data properties (sanitized)
              data_keys: data ? Object.keys(data) : [],
            },
            organizationId
          )
        }
        
        return result
      } catch (error) {
        const duration = Date.now() - startTime
        
        // Track failed mutation with any additional context added by the handler
        if (userId) {
          await trackMutation(
            `${functionName}_failed`,
            userId,
            {
              ...trackingContext,
              duration_ms: duration,
              success: false,
              error_message: error instanceof Error ? error.message : 'Unknown error',
              error_type: error instanceof Error ? error.constructor.name : 'Unknown',
              // Include relevant data properties (sanitized)
              data_keys: data ? Object.keys(data) : [],
            },
            organizationId
          )
        }
        
        throw error
      }
    })
}

/**
 * Middleware to require user to be part of at least one active organization.
 * Admins with AdminOrgRead scope bypass this check.
 * Uses internal APIs directly — server functions cannot be called from middleware.
 */
export const requireActiveOrganization = createMiddleware({ type: 'function' })
  .middleware([requireAuth])
  .server(async ({ next, context }) => {
    const hasAdminScope = testScopes(context.accessToken?.scopes ?? [], LogtoScopes.AdminOrgRead)

    if (hasAdminScope) {
      return await next()
    }

    const { LogtoManagementApi } = await import('~/lib/logto/logto-management-api')
    const { fetchOrganizationData } = await import('~/functions/orgs/getOrg')

    const userOrgs = await LogtoManagementApi.getUserOrganizations(context.accessToken.sub)

    const statuses = await Promise.all(
      userOrgs.map(async (org) => {
        try {
          const details = await fetchOrganizationData(org.id)
          return details?.status
        } catch {
          return undefined
        }
      }),
    )

    const hasActiveOrg = statuses.some(
      (status) => status === 'Active' || status === 'Internal',
    )

    if (!hasActiveOrg) {
      throw new ErrorResponse(
        'No active organizations available. You must be part of an active organization to access documentation.',
        403,
        'NO_ACTIVE_ORG'
      )
    }

    return await next()
  })
